import { useEffect, useState } from "react";
import { API } from "../../config";

const TIERS = [
    { lookup_key: "chip_in_coffee", amount: "A$5", label: "COFFEE SUBSYSTEM REFILL" },
    { lookup_key: "chip_in_gpu", amount: "A$25", label: "GPU FUND CONTRIBUTION" },
    { lookup_key: "chip_in_spark", amount: "A$100", label: "DGX SPARK SERIOUS CONTRIBUTION" },
    { lookup_key: "chip_in_custom", amount: "A$???", label: "CUSTOM AMOUNT — WHOLE SPARKS ACCEPTED" },
];

const WISHLIST = [
    {
        item: "NVIDIA DGX Spark",
        qty: "1 (2 PREFERRED)",
        justification: "Local AI experiments. Current hardware has formally asked to be excused.",
    },
    {
        item: "NVIDIA RTX PRO 6000",
        qty: "1",
        justification: "VRAM headroom for unnecessarily large models. See: Experiment EXP_03.",
    },
    {
        item: "ESPRESSO-GRADE COFFEE",
        qty: "RECURRING",
        justification: "Critical subsystem. See incident log 04:12:07.",
    },
];

export default function FundingSection() {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState(null);
    const [funds, setFunds] = useState(null);
    const [donorName, setDonorName] = useState("");
    const [wallOptIn, setWallOptIn] = useState(false);

    useEffect(() => {
        const load = () =>
            fetch(`${API}/api/funds`)
                .then((r) => (r.ok ? r.json() : null))
                .then((data) => data && setFunds(data))
                .catch(() => {});
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, []);

    const donate = async (tier) => {
        setLoading(tier.lookup_key);
        setError(null);
        try {
            const r = await fetch(`${API}/api/payments/checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    lookup_key: tier.lookup_key,
                    quantity: 1,
                    origin_url: window.location.origin,
                    donor_name: donorName,
                    wall_opt_in: wallOptIn,
                }),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => null);
                throw new Error(data?.detail || "DONATION RAIL HICCUP — TRY AGAIN");
            }
            const data = await r.json();
            window.location.href = data.checkout_url;
        } catch (e) {
            setError(e.message && e.message !== "checkout failed" ? e.message : "DONATION RAIL HICCUP — TRY AGAIN");
            setLoading(null);
        }
    };

    const sparkRaised = funds ? funds.spark.raised_cents / 100 : 0;
    const sparkGoal = funds ? funds.spark.goal_cents / 100 : 8000;
    const sparkPct = Math.min(100, (sparkRaised / sparkGoal) * 100);
    const coffeeReserves = funds ? funds.coffee.reserves : 0;

    return (
        <section id="funding" className="py-20 border-b border-subtle bg-[#e9edf1]" data-testid="funding-section">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="p-8 sm:p-12 rounded-xl bg-white border border-subtle relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
                    <span className="font-mono text-xs text-cyan-600 tracking-widest block uppercase mb-2">
                        // Capital Request // Series Whatever
                    </span>
                    <h2 className="font-display text-2xl sm:text-4xl font-bold text-slate-900 mb-4">
                        SEEKING VENTURE CAPITAL.
                    </h2>
                    <p className="text-slate-600 text-base sm:text-lg mb-3 leading-relaxed max-w-3xl">
                        TIMBOTIC has raised $0 to date and is prepared to accept substantially more. Funds will be
                        allocated with the same rigour that produced the 4 AM coffee incident.
                    </p>
                    <p className="text-slate-500 text-sm mb-10 leading-relaxed max-w-3xl">
                        No equity offered. No roadmap promised. Things will be built.
                    </p>

                    <div className="font-mono text-xs">
                        <div className="text-cyan-600 font-bold border-b border-slate-200 pb-2 mb-2 flex justify-between">
                            <span>PROCUREMENT WISHLIST // FORM PW-001</span>
                            <span className="text-slate-500">PRIORITY: YES</span>
                        </div>
                        {WISHLIST.map((w, i) => (
                            <div
                                key={w.item}
                                data-testid={`wishlist-item-${i + 1}`}
                                className="py-3 border-b border-slate-200 grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-4"
                            >
                                <span className="sm:col-span-4 text-slate-900 font-bold">{w.item}</span>
                                <span className="sm:col-span-2 text-amber-600">{w.qty}</span>
                                <span className="sm:col-span-6 text-slate-500">{w.justification}</span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 pt-6 border-t border-slate-200 font-mono text-xs space-y-6" data-testid="funds-telemetry">
                        <div>
                            <div className="flex justify-between mb-2">
                                <span className="text-slate-500">DGX SPARK FUND</span>
                                <span data-testid="spark-fund-amount" className="text-slate-900 font-bold">
                                    A${sparkRaised.toFixed(2)} OF A$8,000
                                </span>
                            </div>
                            <div className="h-3 rounded bg-slate-100 border border-subtle overflow-hidden">
                                <div
                                    data-testid="spark-fund-bar"
                                    className="h-full bg-cyan-500 transition-all duration-1000"
                                    style={{ width: `${Math.max(sparkPct, sparkRaised > 0 ? 1 : 0)}%` }}
                                />
                            </div>
                            <div className="mt-1.5 text-[10px] text-slate-500">
                                {sparkPct.toFixed(2)}% FUNDED // PROBABILITY OF TWO SPARKS: RISING
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between mb-2">
                                <span className="text-slate-500">COFFEE RESERVES</span>
                                <span
                                    data-testid="coffee-reserves-count"
                                    className={coffeeReserves > 0 ? "text-emerald-600 font-bold" : "text-red-600 font-bold"}
                                >
                                    {coffeeReserves} UNITS
                                </span>
                            </div>
                            <div className="flex gap-1" data-testid="coffee-reserves-gauge">
                                {Array.from({ length: 20 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className={`h-3 flex-1 rounded-sm ${
                                            i < Math.min(coffeeReserves, 20)
                                                ? "bg-amber-500"
                                                : "bg-slate-100 border border-slate-200"
                                        }`}
                                    />
                                ))}
                            </div>
                            <div className="mt-1.5 text-[10px] text-slate-500">
                                {coffeeReserves === 0
                                    ? "STATUS: CRITICAL. EVERY A$5 ADDS ONE UNIT."
                                    : coffeeReserves > 20
                                      ? "STATUS: SURPLUS. TIM IS VIBRATING."
                                      : "STATUS: TEMPORARILY STABLE. EVERY A$5 ADDS ONE UNIT."}
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            data-testid="donate-now-button"
                            className="px-8 py-4 rounded bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-mono font-bold text-sm tracking-wider uppercase transition-colors shadow-[0_0_25px_rgba(6,182,212,0.4)]"
                        >
                            Donate Now
                        </button>
                        <span className="font-mono text-[11px] text-slate-500">
                            VALUATION: OPTIMISTIC // DILIGENCE: SELF-CONDUCTED
                        </span>
                    </div>

                    <div className="mt-8 pt-6 border-t border-slate-200 font-mono text-xs" data-testid="donor-wall">
                        <div className="text-cyan-600 font-bold mb-3">
                            SUPPORTERS OF QUESTIONABLE ENGINEERING
                        </div>
                        {!funds || funds.wall.length === 0 ? (
                            <div className="text-slate-500 italic">
                                [ NO SUPPORTERS ON RECORD — THE WALL WAITS ]
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {funds.wall.map((w, i) => (
                                    <div
                                        key={`${w.name}-${i}`}
                                        data-testid={`donor-wall-entry-${i + 1}`}
                                        className="flex justify-between gap-4 text-slate-600"
                                    >
                                        <span className="text-slate-900 font-semibold">{w.name}</span>
                                        <span className="text-slate-500">{w.tier}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="mt-3 text-[10px] text-slate-500">
                            OPT-IN ONLY. ANONYMITY RESPECTED. JUDGEMENT WITHHELD.
                        </div>
                    </div>
                </div>
            </div>

            {open && (
                <div
                    data-testid="donation-dialog"
                    className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
                    onClick={() => !loading && setOpen(false)}
                >
                    <div
                        className="w-full max-w-md rounded-lg border border-subtle bg-white p-8 font-mono shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="text-cyan-600 text-[10px] tracking-[0.35em] uppercase mb-2 text-center">
                            Timbotic Procurement Portal
                        </div>
                        <div className="text-emerald-600 text-sm font-bold mb-2 text-center flex items-center justify-center gap-2">
                            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-led" />
                            DONATION RAIL: ONLINE
                        </div>
                        <p className="text-slate-500 text-[11px] leading-relaxed mb-6 text-center">
                            Secure checkout via Stripe. No equity offered. Things will be built.
                        </p>

                        <div className="space-y-3">
                            {TIERS.map((t) => (
                                <button
                                    key={t.lookup_key}
                                    type="button"
                                    disabled={loading !== null}
                                    onClick={() => donate(t)}
                                    data-testid={`donate-tier-${t.lookup_key}`}
                                    className="w-full flex items-center justify-between px-4 py-3.5 rounded border border-subtle bg-slate-50 hover:border-cyan-500/50 hover:bg-cyan-50 transition-colors disabled:opacity-60 text-left"
                                >
                                    <span>
                                        <span className="block text-slate-900 font-bold text-sm">{t.amount}</span>
                                        <span className="block text-[10px] text-slate-500 mt-0.5">{t.label}</span>
                                    </span>
                                    <span className="text-cyan-600 text-xs font-bold">
                                        {loading === t.lookup_key ? "ROUTING..." : "DONATE ->"}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {error && (
                            <div data-testid="donation-error" className="mt-4 text-center text-[11px] text-red-600">
                                {error}
                            </div>
                        )}

                        <div className="mt-6 pt-5 border-t border-slate-200 space-y-3">
                            <input
                                type="text"
                                value={donorName}
                                onChange={(e) => setDonorName(e.target.value)}
                                maxLength={40}
                                placeholder="DISPLAY NAME (OPTIONAL)"
                                data-testid="donor-name-input"
                                className="w-full px-3 py-2.5 rounded border border-subtle bg-slate-50 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-cyan-500/60"
                            />
                            <label className="flex items-start gap-2.5 text-[11px] text-slate-500 cursor-pointer leading-snug">
                                <input
                                    type="checkbox"
                                    checked={wallOptIn}
                                    onChange={(e) => setWallOptIn(e.target.checked)}
                                    data-testid="wall-opt-in-checkbox"
                                    className="mt-0.5 accent-cyan-600"
                                />
                                ADD ME TO THE WALL — PUBLICLY ACKNOWLEDGE MY QUESTIONABLE DECISION
                            </label>
                        </div>

                        <div className="mt-6 text-center">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                disabled={loading !== null}
                                data-testid="donation-close-button"
                                className="text-[10px] tracking-widest text-slate-500 hover:text-slate-700 transition-colors"
                            >
                                [ CLOSE ]
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
